import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FileText, Plus, Search, Edit2, Trash2, Star } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import RichTextEditor from '../../components/UI/RichTextEditor';

const stripHtml = (html: string) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

interface TermsAndCondition {
  _id: string;
  name: string;
  content: string;
  category: string;
  isDefault: boolean;
  isActive: boolean;
}

const TermsAndConditionsPage: React.FC = () => {
  const [items, setItems] = useState<TermsAndCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TermsAndCondition | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: 'general',
    isDefault: false,
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTermsAndConditions();
      setItems(response.data || []);
    } catch (error) {
      console.error('Error fetching terms and conditions:', error);
      toast.error('Failed to fetch terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (item?: TermsAndCondition) => {
    if (item) {
      setModalOpen(true);
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        content: item.content || '',
        category: item.category || 'general',
        isDefault: item.isDefault || false,
        isActive: item.isActive !== undefined ? item.isActive : true,
      });
      try {
        const res = await apiService.getTermsAndCondition(item._id);
        const full = res.data;
        if (full?.content !== undefined) {
          setFormData((prev) => ({ ...prev, content: full.content || '' }));
        }
      } catch {
        // keep formData from list item
      }
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        content: '',
        category: 'general',
        isDefault: false,
        isActive: true,
      });
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({
      name: '',
      content: '',
      category: 'general',
      isDefault: false,
      isActive: true,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingItem) {
        await apiService.updateTermsAndCondition(editingItem._id, formData);
        toast.success('Terms and conditions updated successfully');
      } else {
        await apiService.createTermsAndCondition(formData);
        toast.success('Terms and conditions created successfully');
      }
      handleCloseModal();
      fetchItems();
    } catch (error: any) {
      console.error('Error saving terms and conditions:', error);
      toast.error(error.response?.data?.message || 'Failed to save terms and conditions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this terms and conditions?')) {
      return;
    }

    try {
      await apiService.deleteTermsAndCondition(id);
      toast.success('Terms and conditions deleted successfully');
      fetchItems();
    } catch (error: any) {
      console.error('Error deleting terms and conditions:', error);
      toast.error(error.response?.data?.message || 'Failed to delete terms and conditions');
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stripHtml(item.content || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      header: 'Name',
      accessor: (row: TermsAndCondition) => (
        <div className="flex items-center">
          {row.isDefault && (
            <Star className="w-4 h-4 mr-2 text-yellow-500 fill-yellow-500" />
          )}
          <div>
            <div className="text-sm font-medium text-gray-900">{row.name}</div>
            <div className="text-sm text-gray-500 truncate max-w-xs">{stripHtml(row.content)}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Category',
      accessor: (row: TermsAndCondition) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.category === 'quotation' ? 'bg-blue-100 text-blue-800' :
          row.category === 'boq' ? 'bg-green-100 text-green-800' :
          row.category === 'invoice' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {row.category.toUpperCase()}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: (row: TermsAndCondition) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: TermsAndCondition) => (
        <div className="flex gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            icon={<Edit2 className="w-4 h-4" />}
            onClick={() => handleOpenModal(row)}
          />
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(row._id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
            Terms & Conditions
          </h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>
            Manage terms and conditions templates
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Button
            icon={<Plus className="w-4 h-4" />}
            onClick={() => handleOpenModal()}
          >
            Add Terms & Conditions
          </Button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card padding="md">
          <Input
            placeholder="Search terms and conditions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="w-5 h-5" />}
            fullWidth
          />
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card padding="none">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredItems.length > 0 ? (
            <Table
              data={filteredItems}
              columns={columns}
              emptyMessage="No terms and conditions found"
            />
          ) : (
            <div className="px-6 py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                No terms and conditions yet
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Get started by creating your first terms and conditions template.
              </p>
              <div className="mt-6">
                <Button
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => handleOpenModal()}
                >
                  Add Terms & Conditions
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Edit Terms & Conditions' : 'Add New Terms & Conditions'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter name"
            required
            fullWidth
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="form-select"
            >
              <option value="general">General</option>
              <option value="quotation">Quotation</option>
              <option value="boq">BOQ</option>
              <option value="invoice">Invoice</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <RichTextEditor
              key={editingItem?._id ?? 'new'}
              value={formData.content}
              onChange={(content) => setFormData({ ...formData, content })}
              placeholder="Enter terms and conditions content (use toolbar for bold, italic, underline, strikethrough)"
              minHeight="200px"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Set as default</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
            >
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TermsAndConditionsPage;

